'use client';

import { c as _c } from "react/compiler-runtime";
import './feature-buttons.scss';
import type { ElementType } from 'react';
import { useState } from 'react';
import { z } from 'zod';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { IconNamesSafe } from '@sola/schema/lib/icons/icon-names.ts';
import type { Link as LinkSchema } from '@sola/schema/lib/schema.ts';
import { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { isAnchorLink } from '@sola/utils/url.ts';
import { isPlaylistLink, isVideoLink } from '@sola/utils/video.ts';
import { handleAnchorClick } from '../../utils/anchor.ts';
import { Lightbox, LightboxTrigger } from '../lightbox/lightbox.tsx';
enum DisplayAs {
  Links = 'links',
  Buttons = 'buttons',
}
interface CTAProps {
  cta: LinkSchema;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'link';
  iconName?: IconNames;
  iconPlacement: 'before' | 'after';
  buttonSize: 'sm' | 'md' | 'lg' | 'xl';
  displayAs: DisplayAs;
  onVideoClick?: (index: number) => void;
  videoIndex?: number;
}
export const FeatureButtonsProps = FlexibleContentProps.extend({
  buttonSize: z.enum(['sm', 'md', 'lg', 'xl']).optional(),
  primaryCTAVariant: z.enum(['primary', 'secondary', 'tertiary']).optional(),
  primaryCTAIconName: IconNamesSafe.optional(),
  primaryCTAIconPlacement: z.enum(['before', 'after']).optional(),
  secondaryCTAVariant: z.enum(['primary', 'secondary', 'tertiary']).optional(),
  secondaryCTAIconName: IconNamesSafe.optional(),
  secondaryCTAIconPlacement: z.enum(['before', 'after']).optional(),
  tertiaryCTAVariant: z.enum(['tertiary', 'link']).optional(),
  tertiaryCTAIconName: IconNamesSafe.optional(),
  tertiaryCTAIconPlacement: z.enum(['before', 'after']).optional(),
  variant: z.enum(['default', 'stacked', 'grow', 'noWrap']).optional(),
  alignment: z.enum(['left', 'right', 'center', 'default']).optional(),
  isLinks: z.boolean().optional(),
  inline: z.boolean().optional(),
  className: z.string().optional()
});
export type FeatureButtonsProps = z.infer<typeof FeatureButtonsProps>;
const buttonSpacings = {
  sm: {
    button: 'gap-snug-lg',
    link: 'gap-comfy-sm'
  },
  md: {
    button: 'gap-snug-lg',
    link: 'gap-comfy-md'
  },
  lg: {
    button: 'gap-snug-lg',
    link: 'gap-comfy-lg'
  },
  xl: {
    button: 'gap-snug-lg',
    link: 'gap-comfy-lg'
  }
};
const getButtonSpacing = (size: FeatureButtonsProps['buttonSize'] = 'md', variant: FeatureButtonsProps['variant'] = 'default') => {
  const baseSpacings = {
    sm: {
      button: 'gap-snug-lg',
      link: 'gap-comfy-sm'
    },
    md: {
      button: 'gap-snug-lg',
      link: 'gap-comfy-md'
    },
    lg: {
      button: 'gap-snug-lg',
      link: 'gap-comfy-lg'
    },
    xl: {
      button: 'gap-snug-lg',
      link: 'gap-comfy-lg'
    }
  };
  const variantOverrides: Partial<Record<Exclude<FeatureButtonsProps['variant'], undefined>, Partial<(typeof baseSpacings)['md']>>> = {
    grow: {
      button: 'gap-comfy-xs @sm:gap-snug-lg'
    },
    stacked: {
      button: 'gap-comfy-xs @sm:gap-snug-lg'
    }
  };
  const spacing = baseSpacings[size];
  const overrides = variantOverrides[variant] ?? {};
  return {
    button: overrides.button ?? spacing.button,
    link: overrides.link ?? spacing.link
  };
};
const variantClasses = {
  default: 'flex flex-row items-center @sm:items-center @sm:flex-row @sm:justify-center flex-wrap',
  stacked: 'flex flex-col',
  grow: 'flex flex-col items-center @sm:items-center @sm:flex-row @sm:justify-center flex-wrap',
  noWrap: 'flex flex-col items-center @sm:flex-row @sm:justify-center'
};
const renderCTA = ({
  cta,
  variant,
  iconName,
  iconPlacement,
  buttonSize,
  displayAs,
  onVideoClick,
  videoIndex
}: CTAProps) => {
  const shouldRenderAsLink = displayAs === DisplayAs.Links || variant === 'link';
  const isVideo = isVideoLink(cta.link);
  const isPlaylist = isPlaylistLink(cta.link);
  const linkTarget = isPlaylist ? '_blank' : cta.target;
  const linkRel = isPlaylist ? 'noopener noreferrer' : undefined;
  if (shouldRenderAsLink) {
    const linkContent = <LinkStyle size={buttonSize} iconAfter={iconPlacement === 'after' ? iconName || 'arrow-right' : undefined} iconBefore={iconPlacement === 'before' ? iconName : undefined} noPadding noUnderline inline>
        {isVideo ? <span>{cta.title}</span> : <Link download={cta.isDownload} href={cta.link} target={linkTarget} rel={linkRel} scroll={!isAnchorLink(cta.link)} onClick={e => handleAnchorClick(e, cta.link)}>
            {cta.title}
          </Link>}
      </LinkStyle>;
    if (isVideo) {
      return <LightboxTrigger stopPropagation onClick={() => onVideoClick?.(videoIndex || 0)}>
          {linkContent}
        </LightboxTrigger>;
    }
    return linkContent;
  }
  const buttonContent = <ButtonStyle variant={variant} size={buttonSize}>
      {isVideo ? <div>
          <ArkIcon iconName="play-circle" />
          {cta.title}
        </div> : <Link download={cta.isDownload} href={cta.link} target={linkTarget} rel={linkRel} scroll={!isAnchorLink(cta.link)} onClick={e => handleAnchorClick(e, cta.link)} title={cta.title}>
          {iconName && iconPlacement === 'before' && <ArkIcon iconName={iconName} />}
          {cta.title}
          {iconName && iconPlacement === 'after' && <ArkIcon iconName={iconName} />}
        </Link>}
    </ButtonStyle>;
  if (isVideo) {
    return <LightboxTrigger onClick={() => onVideoClick?.(videoIndex || 0)} stopPropagation>
        {buttonContent}
      </LightboxTrigger>;
  }
  return buttonContent;
};
export const FeatureButtons: React.FC<FeatureButtonsProps & {
  children?: React.ReactNode | React.ReactNode[];
}> = t0 => {
  const $ = _c(69);
  const {
    buttonSize: t1,
    primaryCTA,
    primaryCTAVariant: t2,
    primaryCTAIconName,
    primaryCTAIconPlacement: t3,
    secondaryCTA,
    secondaryCTAVariant: t4,
    secondaryCTAIconName,
    secondaryCTAIconPlacement: t5,
    tertiaryCTA,
    tertiaryCTAVariant: t6,
    tertiaryCTAIconName: t7,
    tertiaryCTAIconPlacement: t8,
    variant: t9,
    alignment: t10,
    isLinks: t11,
    className,
    children
  } = t0;
  const buttonSize = t1 === undefined ? "lg" : t1;
  const primaryCTAVariant = t2 === undefined ? "primary" : t2;
  const primaryCTAIconPlacement = t3 === undefined ? "before" : t3;
  const secondaryCTAVariant = t4 === undefined ? "secondary" : t4;
  const secondaryCTAIconPlacement = t5 === undefined ? "before" : t5;
  const tertiaryCTAVariant = t6 === undefined ? "tertiary" : t6;
  const tertiaryCTAIconName = t7 === undefined ? "arrow-right" : t7;
  const tertiaryCTAIconPlacement = t8 === undefined ? "after" : t8;
  const variant = t9 === undefined ? "default" : t9;
  const alignment = t10 === undefined ? "default" : t10;
  const isLinks = t11 === undefined ? false : t11;
  const [startIndex, setStartIndex] = useState(0);
  const displayAs = isLinks ? DisplayAs.Links : DisplayAs.Buttons;
  const tertiaryIsLink = tertiaryCTA && (displayAs === DisplayAs.Links || tertiaryCTAVariant === "link" || !primaryCTA && !secondaryCTA || variant === "stacked");
  let T0;
  let handleVideoClick;
  let t12;
  let t13;
  let videoIndex;
  if ($[0] !== alignment || $[1] !== buttonSize || $[2] !== className || $[3] !== isLinks || $[4] !== primaryCTA || $[5] !== primaryCTAVariant || $[6] !== secondaryCTA || $[7] !== startIndex || $[8] !== tertiaryCTA || $[9] !== tertiaryIsLink || $[10] !== variant) {
    const buttonSpacing = isLinks || tertiaryIsLink ? getButtonSpacing(buttonSize, variant).link : getButtonSpacing(buttonSize, variant).button;
    let lightboxItems;
    if ($[16] !== primaryCTA || $[17] !== secondaryCTA || $[18] !== tertiaryCTA) {
      lightboxItems = [];
      videoIndex = 0;
      if (primaryCTA && isVideoLink(primaryCTA.link)) {
        let t14;
        if ($[21] !== primaryCTA.link) {
          t14 = {
            media: {
              video: {
                url: primaryCTA.link
              }
            }
          };
          $[21] = primaryCTA.link;
          $[22] = t14;
        } else {
          t14 = $[22];
        }
        lightboxItems.push(t14);
      }
      if (secondaryCTA && isVideoLink(secondaryCTA.link)) {
        let t14;
        if ($[23] !== secondaryCTA.link) {
          t14 = {
            media: {
              video: {
                url: secondaryCTA.link
              }
            }
          };
          $[23] = secondaryCTA.link;
          $[24] = t14;
        } else {
          t14 = $[24];
        }
        lightboxItems.push(t14);
      }
      if (tertiaryCTA && isVideoLink(tertiaryCTA.link)) {
        let t14;
        if ($[25] !== tertiaryCTA.link) {
          t14 = {
            media: {
              video: {
                url: tertiaryCTA.link
              }
            }
          };
          $[25] = tertiaryCTA.link;
          $[26] = t14;
        } else {
          t14 = $[26];
        }
        lightboxItems.push(t14);
      }
      $[16] = primaryCTA;
      $[17] = secondaryCTA;
      $[18] = tertiaryCTA;
      $[19] = lightboxItems;
      $[20] = videoIndex;
    } else {
      lightboxItems = $[19];
      videoIndex = $[20];
    }
    let t14;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = index => {
        setStartIndex(index);
      };
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    handleVideoClick = t14;
    const Comp = lightboxItems.length > 0 ? Lightbox : "div" as ElementType;
    T0 = Comp;
    if ($[28] !== lightboxItems || $[29] !== startIndex) {
      t13 = lightboxItems.length > 0 && {
        items: lightboxItems,
        startIndex
      };
      $[28] = lightboxItems;
      $[29] = startIndex;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    t12 = cn(`FeatureButtons FeatureButtons--${variant} alignment-${alignment}`, buttonSpacing, variantClasses[variant], primaryCTAVariant === "tertiary" && "FeatureButtons--stacked", tertiaryIsLink && buttonSize === "lg" && "pt-snug-lg", className);
    $[0] = alignment;
    $[1] = buttonSize;
    $[2] = className;
    $[3] = isLinks;
    $[4] = primaryCTA;
    $[5] = primaryCTAVariant;
    $[6] = secondaryCTA;
    $[7] = startIndex;
    $[8] = tertiaryCTA;
    $[9] = tertiaryIsLink;
    $[10] = variant;
    $[11] = T0;
    $[12] = handleVideoClick;
    $[13] = t12;
    $[14] = t13;
    $[15] = videoIndex;
  } else {
    T0 = $[11];
    handleVideoClick = $[12];
    t12 = $[13];
    t13 = $[14];
    videoIndex = $[15];
  }
  let t14;
  if ($[31] !== buttonSize || $[32] !== displayAs || $[33] !== handleVideoClick || $[34] !== primaryCTA || $[35] !== primaryCTAIconName || $[36] !== primaryCTAIconPlacement || $[37] !== primaryCTAVariant || $[38] !== videoIndex) {
    t14 = primaryCTA && renderCTA({
      cta: primaryCTA,
      variant: primaryCTAVariant,
      iconName: primaryCTA.icon ? IconNamesSafe.parse(primaryCTA.icon) : primaryCTAIconName,
      iconPlacement: primaryCTA.icon ? "before" : primaryCTAIconPlacement,
      buttonSize,
      displayAs,
      onVideoClick: handleVideoClick,
      videoIndex: isVideoLink(primaryCTA.link) ? videoIndex++ : undefined
    });
    $[31] = buttonSize;
    $[32] = displayAs;
    $[33] = handleVideoClick;
    $[34] = primaryCTA;
    $[35] = primaryCTAIconName;
    $[36] = primaryCTAIconPlacement;
    $[37] = primaryCTAVariant;
    $[38] = videoIndex;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== buttonSize || $[41] !== displayAs || $[42] !== handleVideoClick || $[43] !== secondaryCTA || $[44] !== secondaryCTAIconName || $[45] !== secondaryCTAIconPlacement || $[46] !== secondaryCTAVariant || $[47] !== videoIndex) {
    t15 = secondaryCTA && renderCTA({
      cta: secondaryCTA,
      variant: secondaryCTAVariant,
      iconName: secondaryCTA.icon ? IconNamesSafe.parse(secondaryCTA.icon) : secondaryCTAIconName,
      iconPlacement: secondaryCTA.icon ? "before" : secondaryCTAIconPlacement,
      buttonSize,
      displayAs,
      onVideoClick: handleVideoClick,
      videoIndex: isVideoLink(secondaryCTA.link) ? videoIndex++ : undefined
    });
    $[40] = buttonSize;
    $[41] = displayAs;
    $[42] = handleVideoClick;
    $[43] = secondaryCTA;
    $[44] = secondaryCTAIconName;
    $[45] = secondaryCTAIconPlacement;
    $[46] = secondaryCTAVariant;
    $[47] = videoIndex;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== buttonSize || $[50] !== displayAs || $[51] !== handleVideoClick || $[52] !== tertiaryCTA || $[53] !== tertiaryCTAIconName || $[54] !== tertiaryCTAIconPlacement || $[55] !== tertiaryCTAVariant || $[56] !== tertiaryIsLink || $[57] !== videoIndex) {
    t16 = tertiaryCTA && renderCTA({
      cta: tertiaryCTA,
      variant: tertiaryIsLink ? "link" : tertiaryCTAVariant,
      iconName: tertiaryCTA.icon ? IconNamesSafe.parse(tertiaryCTA.icon) : tertiaryCTAIconName,
      iconPlacement: tertiaryCTA.icon ? "before" : tertiaryCTAIconPlacement,
      buttonSize,
      displayAs,
      onVideoClick: handleVideoClick,
      videoIndex: isVideoLink(tertiaryCTA.link) ? videoIndex++ : undefined
    });
    $[49] = buttonSize;
    $[50] = displayAs;
    $[51] = handleVideoClick;
    $[52] = tertiaryCTA;
    $[53] = tertiaryCTAIconName;
    $[54] = tertiaryCTAIconPlacement;
    $[55] = tertiaryCTAVariant;
    $[56] = tertiaryIsLink;
    $[57] = videoIndex;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== children || $[60] !== t12 || $[61] !== t14 || $[62] !== t15 || $[63] !== t16) {
    t17 = <div className={t12}>{t14}{t15}{t16}{children}</div>;
    $[59] = children;
    $[60] = t12;
    $[61] = t14;
    $[62] = t15;
    $[63] = t16;
    $[64] = t17;
  } else {
    t17 = $[64];
  }
  let t18;
  if ($[65] !== T0 || $[66] !== t13 || $[67] !== t17) {
    t18 = <T0 {...t13}>{t17}</T0>;
    $[65] = T0;
    $[66] = t13;
    $[67] = t17;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  return t18;
};